import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TablePagination,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  GET_PASS_LIST,
  GET_UNIQUE_PASS_INSTRUCTORS,
  GET_MY_PURCHASED_PASSES,
} from "../graphql/query";
import { DOWNLOAD_PASS_INVOICE } from "../graphql/mutation";
import type {
  GetPassListResponse,
  PassListItem,
  PassLinkedOffer,
} from "../interfaces/pass.interface";
import type {
  DownloadPassInvoiceResponse,
  GetMyPurchasedPassesResponse,
  StudentPassPurchase,
} from "../interfaces/studentPassPurchase.interface";
import PassesToolBar from "./PassesToolBar";
import CustomPaginationActions from "./CustomPaginationActions";
import CustomSnackbar from "./Snackbar";
import themestyle from "../theme";
import { useCart } from "../context/CartContext";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PassPurchaseDrawer from "./PassPurchaseDrawer";
import { getActivePassOffer } from "../utils/offerPricing";
import TruncatedTitle from "./TruncatedTitle";

type Tab = "available" | "my_passes";

type UserPassesCardViewProps = {
  onView: (id: string) => void;
  refreshTrigger?: number;
  initialTab?: Tab;
  bookingContext?: {
    classId: string;
    sessionDate?: string;
    className?: string;
  } | null;
};

const PASS_TYPE_LABELS: Record<string, string> = {
  "Drop-in": "Drop In",
  "Weekly": "Weekly",
  "Monthly": "Monthly",
  DROP_IN: "Drop In",
  WEEKLY_4_CLASSES: "Weekly",
  MONTHLY_4_CLASSES: "Monthly",
};

const VALIDITY_LABELS: Record<string, string> = {
  "Drop-in": "Valid for 1 Week",
  "Weekly": "Valid for 1 Week",
  "Monthly": "Valid for 1 Month",
  DROP_IN: "Valid for 1 Week",
  WEEKLY_4_CLASSES: "Valid for 1 Week",
  MONTHLY_4_CLASSES: "Valid for 1 Month",
};

const calculateRemainingValidity = (purchasedAt: string | Date | undefined, passType: string | undefined) => {
  if (!purchasedAt) return null;

  try {
    const purchaseDate = new Date(purchasedAt);
    let daysValid = 0;
    const type = (passType || "").toLowerCase();
    
    if (type.includes("monthly") || type.includes("month")) daysValid = 30;
    else if (type.includes("weekly") || type.includes("week") || type.includes("drop")) daysValid = 7;
    else if (type.includes("10-20")) daysValid = 30;

    if (daysValid === 0) return null;

    const expiryDate = new Date(purchaseDate);
    expiryDate.setDate(expiryDate.getDate() + daysValid);
    expiryDate.setHours(23, 59, 59, 999);

    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();

    if (diffMs <= 0) return "Expired";

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays === 30) return "1 Month";
    if (diffDays === 7) return "1 Week";
    if (diffDays > 0) {
      return `${diffDays} Day${diffDays > 1 ? "s" : ""}`;
    }
    if (diffHours > 0) {
      return `${diffHours} Hr${diffHours > 1 ? "s" : ""}`;
    }
    return "Less than 1 hour";
  } catch (error) {
    console.error("Error calculating validity:", error);
    return null;
  }
};

const normalizePayableAmount = (amount: number) => {
  if (amount <= 0) return 0;
  return Math.max(1, Number(amount.toFixed(2)));
};

const UserPassesCardView: React.FC<UserPassesCardViewProps> = ({
  initialTab,
  refreshTrigger,
  bookingContext,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState<Tab>(initialTab === "my_passes" ? "my_passes" : "available");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 3 : 9);
  const [searchQuery, setSearchQuery] = useState("");
  const [passType, setPassType] = useState<string | null>(null);
  const [instructorName, setInstructorName] = useState<string | null>(null);
  const [selectedPass, setSelectedPass] = useState<PassListItem | null>(null);
  const [purchaseDrawerOpen, setPurchaseDrawerOpen] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  useEffect(() => {
    setRowsPerPage(isMobile ? 3 : 9);
    setPage(0);
  }, [isMobile]);

  const { data, loading, refetch } = useQuery<GetPassListResponse>(GET_PASS_LIST, {
    variables: {
      page: page + 1,
      perPage: rowsPerPage,
      search: searchQuery || "",
      styleType: passType || "",
      instructorName: instructorName || "",
      status: "Active",
    },
    fetchPolicy: "network-only",
    skip: activeTab !== "available",
  });

  const { data: instructorsData } = useQuery(GET_UNIQUE_PASS_INSTRUCTORS);
  const instructorsList = instructorsData?.getUniquePassInstructors || [];

  const {
    data: myPassesData,
    loading: myPassesLoading,
    refetch: refetchMyPasses,
  } = useQuery<GetMyPurchasedPassesResponse>(GET_MY_PURCHASED_PASSES, {
    fetchPolicy: "cache-and-network",
  });

  const { addItem, isInCart, removeItem } = useCart();
  const [downloadPassInvoice] = useMutation<DownloadPassInvoiceResponse>(DOWNLOAD_PASS_INVOICE);

  React.useEffect(() => {
    if (refreshTrigger) {
      refetch();
      refetchMyPasses();
    }
  }, [refreshTrigger, refetch, refetchMyPasses]);

  React.useEffect(() => {
    if (initialTab === "my_passes") {
      setActiveTab("my_passes");
    }
  }, [initialTab]);

  const passes = useMemo(() => data?.getPassList?.data ?? [], [data]);
  React.useEffect(() => {
    const activePasses = myPassesData?.getMyPurchasedPasses?.data?.filter(
      (p) => ["ACTIVE", "EXPIRED"].includes(p.status)
    ) || [];
    passes.forEach((pass: any) => {
      const isBooked = activePasses.some((p: any) => p.passDbId === pass.passId && p.status === "ACTIVE");
      if (isBooked && isInCart(pass.id)) {
        removeItem(pass.id);
      }
    });
  }, [passes, myPassesData, isInCart, removeItem]);
  const totalItems = data?.getPassList?.totalCount ?? 0;
  const myPasses = (myPassesData?.getMyPurchasedPasses?.data ?? []).filter(
    (p: any) => p.status !== "FAILED" && p.status !== "PENDING"
  );

  const handleOpenPurchaseDrawer = (pass: PassListItem) => {
    setSelectedPass(pass);
    setPurchaseDrawerOpen(true);
  };

  const handlePurchaseComplete = (message: string, type: "success" | "error" | "info") => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);

    if (type === "success") {
      refetchMyPasses();
      refetch();
      setTimeout(() => {
        setPurchaseDrawerOpen(false);
        setSelectedPass(null);
      }, 1200);
    }
  };

  const isSelectedPassBooked = useMemo(() => {
    if (!selectedPass || !myPassesData?.getMyPurchasedPasses?.data) return false;
    return myPassesData.getMyPurchasedPasses.data.some(
      (p: any) => p.passDbId === selectedPass.passId && p.status === "ACTIVE"
    );
  }, [selectedPass, myPassesData]);

  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    value: number
  ) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadInvoice = async (purchase: StudentPassPurchase) => {
    try {
      setDownloadingInvoiceId(purchase.id);
      const { data } = await downloadPassInvoice({
        variables: { purchaseId: purchase.id },
      });

      const result = data?.downloadPassInvoice;
      if (!result?.success || !result.contentBase64) {
        throw new Error(result?.message || "Failed to download invoice");
      }

      const byteCharacters = window.atob(result.contentBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: result.mimeType || "application/pdf",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = result.filename || `Invoice_${purchase.orderId || purchase.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      setSnackbarMessage(result.message || "Invoice downloaded successfully");
      setSnackbarType("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error.message || "Failed to download invoice");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          backgroundColor: themestyle.colors.white,
          borderRadius: "10px",
          padding: "6px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          width: { xs: "100%", md: "fit-content" },
          ml: { md: "auto" },
          mb: 3,
        }}
      >
        <Box
          onClick={() => setActiveTab("available")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderRadius: "8px",
            fontSize: { xs: "13px", sm: "15px" },
            fontWeight: 600,
            color: activeTab === "available" ? "#4CAF50" : "#6B7280",
            backgroundColor: activeTab === "available" ? "#EEF7E9" : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          Available Classes
        </Box>
        <Box
          onClick={() => setActiveTab("my_passes")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderRadius: "8px",
            fontSize: { xs: "13px", sm: "15px" },
            fontWeight: 600,
            color: activeTab === "my_passes" ? "#4CAF50" : "#6B7280",
            backgroundColor: activeTab === "my_passes" ? "#EEF7E9" : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          My Passes
          {myPasses.length > 0 && (
            <Chip
              label={myPasses.length}
              size="small"
              sx={{
                height: "18px",
                fontSize: "11px",
                backgroundColor: "#6CBC44",
                color: "#fff",
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          )}
        </Box>
      </Box>

      {activeTab === "available" && (
        <>
          <PassesToolBar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            passTypeValue={passType}
            onPassTypeChange={setPassType}
            instructorName={instructorName}
            onInstructorChange={setInstructorName}
            instructorsList={instructorsList}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#1E7F3D" }} />
            </Box>
          ) : passes.length === 0 ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{ fontSize: { xs: "13px", sm: "1rem" } }}
              >
                No passes available.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(3, 1fr)",
                },
                gap: { xs: 4, sm: 4, md: 5 },
                rowGap: { xs: 4, sm: 5, md: 6 },
              }}
            >
              {passes.map((pass) => {
                const passPriceFormatted = new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 2,
                }).format(pass.passCost || 0);

                const validityLabel = VALIDITY_LABELS[pass.passType] || "Valid for 30 Days";
                const activeOffer = getActivePassOffer(pass.offers as PassLinkedOffer[] | undefined);
                const discountedPrice = activeOffer
                  ? normalizePayableAmount(
                      pass.passCost - (pass.passCost * activeOffer.discount) / 100
                    )
                  : null;
                const discountedFormatted =
                  discountedPrice !== null
                    ? new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                        minimumFractionDigits: 2,
                      }).format(discountedPrice)
                    : null;

                return (
                    <Box
                      key={pass.id}
                      sx={{
                        display: "flex",
                        mb: 4,
                        cursor: "pointer",
                      }}
                      onClick={() => handleOpenPurchaseDrawer(pass)}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          backgroundColor: "#ffffff",
                          borderRadius: "20px",
                          p: 3,
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.04)",
                          overflow: "hidden",
                          border: activeOffer
                            ? "1.5px solid #6CBC44"
                            : "1.5px solid transparent",
                          transition: "transform 0.2s ease, box-shadow 0.2s ease",
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: "100%",
                          minHeight: "220px",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.08)",
                          },
                        }}
                      >
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: "150px",
                          height: "150px",
                          background:
                            "linear-gradient(135deg, transparent 20%, rgba(30, 127, 61, 0.03) 100%)",
                          borderBottomRightRadius: "20px",
                          zIndex: 0,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -30,
                          right: -30,
                          width: "100px",
                          height: "100px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(30, 127, 61, 0.03)",
                          zIndex: 0,
                        }}
                      />

                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <TruncatedTitle
                            text={pass.passName}
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: "#111827",
                              fontSize: "20px",
                              lineHeight: 1.2,
                              mb: 0.5,
                            }}
                          />
                          <Typography
                            sx={{ color: "#6B7280", fontSize: "13px", fontWeight: 500 }}
                          >
                            {pass.noOfClasses}{" "}
                            {pass.noOfClasses === 1 ? "credit" : "credits"}
                          </Typography>
                          {(() => {
                            const linkedClasses =
                              pass.classNames && pass.classNames.length
                                ? pass.classNames
                                : pass.className
                                ? [pass.className]
                                : [];
                            if (linkedClasses.length === 0) return null;
                            return (
                              <TruncatedTitle
                                text={`${linkedClasses.length > 1 ? "Classes" : "Class"}: ${linkedClasses.join(", ")}`}
                                variant="body2"
                                maxLines={2}
                                sx={{
                                  color: "#4CAF50",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  mt: 0.5,
                                }}
                              />
                            );
                          })()}
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#4B5563",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "6px",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {validityLabel}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          mt: "auto",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            mb: 1.5,
                            minHeight: "72px",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                            {activeOffer && discountedFormatted ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#9CA3AF",
                                    textDecoration: "line-through",
                                    lineHeight: 1,
                                    mb: 0.2,
                                  }}
                                >
                                  {passPriceFormatted}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: "#5AA839",
                                    fontSize: "26px",
                                    lineHeight: 1,
                                  }}
                                >
                                  {discountedFormatted}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  color: pass.passCost === 0 ? "#6B7280" : "#111827",
                                  fontSize: "26px",
                                  lineHeight: 1,
                                }}
                              >
                                {pass.passCost === 0 ? "Free" : passPriceFormatted}
                              </Typography>
                            )}
                          </Box>

                          {activeOffer && (
                            <Box sx={{ textAlign: "right" }}>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                  backgroundColor: "#F0FBE8",
                                  border: "1px solid #C6ECA8",
                                  borderRadius: "8px",
                                  px: 1,
                                  py: 0.4,
                                  mb: 0.5,
                                }}
                              >
                                <LocalOfferOutlinedIcon sx={{ fontSize: 12, color: "#5AA839" }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#5AA839" }}>
                                  {activeOffer.discount}% OFF
                                </Typography>
                              </Box>
                              {activeOffer.offerName && (
                                <Typography
                                  sx={{ fontSize: "11px", color: "#374151", fontWeight: 600, display: "block" }}
                                >
                                  {activeOffer.offerName}
                                </Typography>
                              )}
                              {activeOffer.offerCode && (
                                <Typography sx={{ fontSize: "11px", color: "#6B7280", display: "block" }}>
                                  Code:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#374151",
                                      backgroundColor: "#F3F4F6",
                                      px: 0.6,
                                      py: 0.1,
                                      borderRadius: "4px",
                                      fontFamily: "monospace",
                                      fontSize: "11px",
                                    }}
                                  >
                                    {activeOffer.offerCode}
                                  </Box>
                                </Typography>
                              )}
                              {activeOffer.validTill && (
                                <Typography sx={{ fontSize: "10px", color: "#9CA3AF", mt: 0.25 }}>
                                  Till{" "}
                                  {new Date(activeOffer.validTill).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          {(() => {
                            const isBooked = myPasses.some((p: any) => p.passDbId === pass.passId && p.status === "ACTIVE");
                            return (
                              <>
                                <Button
                                  variant="contained"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPurchaseDrawer(pass);
                                  }}
                                  disabled={isBooked || (myPassesLoading && !myPassesData)}
                                  sx={{
                                    flex: 1,
                                    backgroundColor: isBooked ? "#F3F4F6" : "#6CBC44",
                                    color: isBooked ? "#D1D5DB" : "#fff",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: "10px",
                                    py: 1,
                                    boxShadow: "none",
                                    fontSize: "13px",
                                    "&:hover": {
                                      backgroundColor: isBooked ? "#F3F4F6" : "#5AA839",
                                      boxShadow: isBooked ? "none" : "0px 4px 12px rgba(108, 188, 68, 0.2)",
                                    },
                                    "&.Mui-disabled": {
                                      backgroundColor: isBooked ? "#F3F4F6" : "rgba(108, 188, 68, 0.4)",
                                      color: isBooked ? "#9CA3AF" : "#fff"
                                    }
                                  }}
                                >
                                  {isBooked ? "Booked" : (myPassesLoading && !myPassesData ? "..." : "Book Now")}
                                </Button>

                                <Button
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isInCart(pass.id)) {
                                      addItem({
                                        id: pass.id,
                                        type: "pass",
                                        name: pass.passName,
                                        originalPrice: pass.passCost || 0,
                                        finalPrice: discountedPrice !== null ? discountedPrice : (pass.passCost || 0),
                                        discount: activeOffer?.discount,
                                        discountLabel: activeOffer?.offerName,
                                        discountCode: activeOffer?.offerCode,
                                        styleType: PASS_TYPE_LABELS[pass.passType] || pass.passType,
                                        date: pass.classScheduledDates && pass.classScheduledDates.length > 0
                                          ? new Intl.DateTimeFormat("en-US", { weekday: "short", day: "numeric", month: "short" }).format(new Date(pass.classScheduledDates[0]))
                                          : validityLabel,
                                        time: pass.classEndTime || undefined,
                                        image: (pass as any).thumbnail || undefined,
                                      });
                                      setSnackbarMessage(`${pass.passName} added to cart!`);
                                      setSnackbarType("success");
                                      setSnackbarOpen(true);
                                    }
                                  }}
                                  disabled={isBooked}
                                  sx={{
                                    borderColor: isBooked ? "#F3F4F6" : "#6CBC44",
                                    color: isBooked ? "#9CA3AF" : "#6CBC44",
                                    minWidth: "46px",
                                    width: "46px",
                                    p: 0,
                                    borderRadius: "10px",
                                    backgroundColor: isInCart(pass.id) && !isBooked ? "#F0FDE8" : "#fff",
                                    "&:hover": {
                                      backgroundColor: isBooked ? "#fff" : (isInCart(pass.id) ? "#F0FDE8" : "rgba(108, 188, 68, 0.04)"),
                                      borderColor: isBooked ? "#F3F4F6" : "#6CBC44",
                                    },
                                    "&.Mui-disabled": {
                                      borderColor: isInCart(pass.id) && !isBooked ? "#6CBC44" : "#E5E7EB",
                                      color: isInCart(pass.id) && !isBooked ? "#6CBC44" : "#9CA3AF",
                                      backgroundColor: isInCart(pass.id) && !isBooked ? "#F0FDE8" : "#fff",
                                    }
                                  }}
                                >
                                  {isBooked || isInCart(pass.id) ? <CheckCircleIcon fontSize="small" /> : <AddShoppingCartIcon fontSize="small" />}
                                </Button>
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {totalItems > (isMobile ? 3 : 9) && (
            <Box sx={{ mt: 4, mb: 2 }}>
              {isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    py: 2,
                    px: 2,
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #F3F4F6",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "14px", color: "#1F2937", fontWeight: 500, mr: 2 }}
                  >
                    {page * rowsPerPage + 1} –{" "}
                    {Math.min((page + 1) * rowsPerPage, totalItems)}
                    <span style={{ marginLeft: "8px", marginRight: "8px", color: "#9CA3AF" }}>
                      of
                    </span>
                    {totalItems}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      sx={{
                        width: "32px",
                        height: "32px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        color: "#1F2937",
                        "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M10 12L6 8L10 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={(page + 1) * rowsPerPage >= totalItems}
                      sx={{
                        width: "32px",
                        height: "32px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        color: "#1F2937",
                        "&:disabled": { color: "#D1D5DB", borderColor: "#F3F4F6" },
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M6 4L10 8L6 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[9, 18, 27]}
                  ActionsComponent={CustomPaginationActions}
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "13px",
                      color: "#6B7280",
                    },
                    "& .MuiTablePagination-displayedRows": { marginRight: "16px" },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      ml: 2,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}

      {activeTab === "my_passes" && (
        <Box>
          {myPassesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#1E7F3D" }} />
            </Box>
          ) : myPasses.length === 0 ? (
            <Box sx={{ py: 10, textAlign: "center" }}>
              <CardMembershipOutlinedIcon
                sx={{ fontSize: "48px", color: "#D1D5DB", mb: 1 }}
              />
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{ fontSize: { xs: "13px", sm: "1rem" } }}
              >
                You haven't booked any passes yet.
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#9CA3AF", mt: 0.5 }}
              >
                Go to Available Classes to book your first pass.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
                gap: { xs: 4, sm: 4, md: 5 },
                rowGap: { xs: 4, sm: 5, md: 6 },
              }}
            >
              {myPasses.map((purchase: StudentPassPurchase) => {
                const purchasedDateObj = new Date(purchase.purchasedAt);
                const purchasedDate = `${purchasedDateObj.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })} at ${purchasedDateObj.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true
                })}`;
                const isInvoiceDownloadable =
                  purchase.invoiceAvailable &&
                  ["SUCCESS", "PAID"].includes((purchase.paymentStatus || "").toUpperCase());
                const isDownloading = downloadingInvoiceId === purchase.id;
                return (
                  <Box
                    key={purchase.id}
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "20px",
                      p: 3,
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #E9F5E1",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mb: 4,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <TruncatedTitle
                        text={purchase.passName}
                        variant="h6"
                        maxLines={2}
                        sx={{ fontWeight: 800, fontSize: "18px", color: "#111827" }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {(() => {
                          const validity = calculateRemainingValidity(purchase.purchasedAt, purchase.passType);
                          return validity && (
                            <Chip
                              label={validity === "Expired" ? "Expired" : (validity === "Less than 1 hour" ? validity : `Valid for ${validity}`)}
                              size="small"
                              sx={{
                                backgroundColor: validity === "Expired" ? "#FEF3F2" : "#F3F4F6",
                                color: validity === "Expired" ? "#B91C1C" : "#4B5563",
                                fontWeight: 700,
                                fontSize: "11px",
                                height: "22px",
                              }}
                            />
                          );
                        })()}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                        <strong>Pass ID:</strong> {purchase.passDbId}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                        <strong>Type:</strong>{" "}
                        {PASS_TYPE_LABELS[purchase.passType] || purchase.passType}
                      </Typography>
                      {purchase.noOfClasses !== undefined && purchase.noOfClasses > 0 && (
                        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                          <strong>Credits:</strong> {purchase.noOfClasses}
                        </Typography>
                      )}
                      {(() => {
                        const linkedClasses =
                          purchase.classNames && purchase.classNames.length
                            ? purchase.classNames
                            : purchase.className
                            ? [purchase.className]
                            : [];
                        if (linkedClasses.length === 0) return null;
                        return (
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                            <Typography sx={{ fontSize: "13px", color: "#6B7280", fontWeight: 700, whiteSpace: "nowrap" }}>
                              {linkedClasses.length > 1 ? "Classes:" : "Class:"}
                            </Typography>
                            <TruncatedTitle
                              text={linkedClasses.join(", ")}
                              variant="body2"
                              maxLines={2}
                              sx={{ fontSize: "13px", color: "#4CAF50", fontWeight: 600 }}
                            />
                          </Box>
                        );
                      })()}
                      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                        <strong>Booked on:</strong> {purchasedDate}
                      </Typography>
                      {purchase.expiryDate && (
                        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                          <strong>Expires on:</strong> {new Date(purchase.expiryDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })}
                        </Typography>
                      )}
                      {purchase.paymentStatus && (() => {
                        const isPaid = ["SUCCESS", "PAID"].includes(
                          (purchase.paymentStatus || "").toUpperCase(),
                        );
                        // Reflect the actual payment status instead of always "PAID":
                        // green for paid, amber for anything else (pending/failed/…).
                        const palette = isPaid
                          ? { text: "#2E7D32", bg: "#ECF8EE", border: "#CFE8D3" }
                          : { text: "#B45309", bg: "#FEF3C7", border: "#FDE68A" };
                        return (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.8,
                              width: "fit-content",
                              mt: 0.4,
                              px: 1.2,
                              py: 0.7,
                              borderRadius: "999px",
                              backgroundColor: palette.bg,
                              border: `1px solid ${palette.border}`,
                            }}
                          >
                            {isPaid && (
                              <CheckCircleRoundedIcon
                                sx={{ fontSize: 16, color: palette.text }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 800,
                                color: palette.text,
                                letterSpacing: "0.3px",
                              }}
                            >
                              {isPaid ? "PAID" : (purchase.paymentStatus || "").toUpperCase()}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    {isInvoiceDownloadable && (
                      <Box
                        sx={{
                          mt: "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          background: "linear-gradient(135deg, #F7FBF4 0%, #EEF8EA 100%)",
                          border: "1px solid #D9ECD1",
                          borderRadius: "16px",
                          px: 1.5,
                          py: 1.3,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 800,
                              color: "#1F2937",
                              lineHeight: 1.2,
                            }}
                          >
                            Payment Receipt
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#6B7280",
                              mt: 0.4,
                              lineHeight: 1.35,
                            }}
                          >
                            Download your PDF receipt for this pass purchase.
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          aria-label="Download invoice"
                          onClick={() => handleDownloadInvoice(purchase)}
                          disabled={isDownloading}
                          sx={{
                            flexShrink: 0,
                            minWidth: "48px",
                            width: "48px",
                            height: "48px",
                            backgroundColor: "#4CAF50",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: "12px",
                            px: 0,
                            py: 0,
                            boxShadow: "none",
                            "&:hover": {
                              backgroundColor: "#439A47",
                              boxShadow: "0px 8px 18px rgba(76, 175, 80, 0.18)",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#A5D6A7",
                              color: "#FFFFFF",
                            },
                          }}
                        >
                          {isDownloading ? (
                            <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
                          ) : (
                            <DownloadRoundedIcon fontSize="small" />
                          )}
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      <PassPurchaseDrawer
        open={purchaseDrawerOpen}
        pass={selectedPass}
        onClose={() => {
          setPurchaseDrawerOpen(false);
          setSelectedPass(null);
        }}
        onPurchaseComplete={handlePurchaseComplete}
        bookingContext={bookingContext}
        isBooked={isSelectedPassBooked}
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default UserPassesCardView;
